'use client'

import { useDocuments, useDeleteDocument } from '@/hooks/useDocuments'
import {
  useDeleteReaderContent,
  useReaderContents,
  type StoredReaderContent
} from '@/hooks/useReaderContents'
import { Button } from '@/components/ui/button'
import { BodyText } from '@/components/ui/typography'
import { IconFileText, IconTrash, IconLoader2 } from '@tabler/icons-react'
import { DocumentListSkeleton } from '@/components/ui/loading-skeletons'
import { useTranslations } from 'next-intl'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog'
import { useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { LoginDialog } from '@/components/auth/login-dialog'
import {
  getLegacyDocumentStats,
  getReaderStats,
  isReaderContent
} from '@/lib/reader/content'
import type { UserDocument } from '@/types/documents'
import { ContentCard } from '@/components/content-card'

// Format date relative to now
function formatDateRelative(date: string): string {
  const now = new Date()
  const then = new Date(date)
  const diffMs = now.getTime() - then.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
  return then.toLocaleDateString()
}

interface DocumentListProps {
  /** Optional function to generate read URL. Defaults to /read/document/<id> */
  onReadDocument?: (docId: string) => string
}

export function DocumentList({ onReadDocument }: DocumentListProps = {}) {
  const t = useTranslations('reader')
  const tCommon = useTranslations('common')
  const { user } = useAuth()
  const { data: legacyDocuments, isLoading: legacyLoading } = useDocuments()
  const { data: readerDocuments, isLoading: readerLoading } =
    useReaderContents('document')
  const deleteDocument = useDeleteDocument()
  const deleteReaderContent = useDeleteReaderContent()
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const getReadHref =
    onReadDocument ?? ((docId: string) => `/read/document/${docId}`)

  const items = [
    ...(readerDocuments ?? []).map((item) => ({
      kind: 'reader' as const,
      id: item.id,
      title: item.title,
      createdAt: item.last_opened_at || item.updated_at || item.created_at,
      content: item.content,
      source: item
    })),
    ...(legacyDocuments ?? []).map((item) => ({
      kind: 'legacy' as const,
      id: item.id,
      title: item.title,
      createdAt: item.created_at,
      content: item.content,
      source: item
    }))
  ].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  )

  const handleDelete = async (item: DocumentListItem) => {
    setDeletingId(item.id)
    try {
      if (item.kind === 'reader') {
        await deleteReaderContent.mutateAsync(item.id)
      } else {
        await deleteDocument.mutateAsync(item.id)
      }
    } finally {
      setDeletingId(null)
    }
  }

  const makeSubtitle = (item: DocumentListItem) => {
    if (item.kind === 'reader') {
      const stats = getReaderStats(item.content)
      return `${stats.words} words, ${stats.glossary} glossary entries`
    }

    if (isReaderContent(item.content)) {
      const stats = getReaderStats(item.content)
      return `${stats.words} words, ${stats.glossary} glossary entries`
    }

    const stats = getLegacyDocumentStats(item.content)
    return t('documentInfo', {
      paragraphs: stats.paragraphs,
      sentences: stats.sentences
    })
  }

  if (!user) {
    return (
      <div className="text-muted-foreground rounded-lg border border-dashed p-8 text-center text-sm">
        <IconFileText className="mx-auto h-8 w-8" />
        <div className="mx-auto mt-3 max-w-sm">
          <p className="text-foreground font-semibold">
            {t('loginToViewDocuments')}
          </p>
          <BodyText className="text-muted-foreground mt-1 text-sm">
            {t('signInDescription')}
          </BodyText>
        </div>
        <LoginDialog>
          <Button size="sm" className="mt-4">
            {tCommon('signIn')}
          </Button>
        </LoginDialog>
      </div>
    )
  }

  if (legacyLoading || readerLoading) {
    return <DocumentListSkeleton />
  }

  if (items.length === 0) {
    return (
      <div className="space-y-4">
        <div className="text-muted-foreground rounded-lg border border-dashed p-8 text-center text-sm">
          <IconFileText className="text-muted-foreground mx-auto h-8 w-8" />
          <BodyText className="text-muted-foreground mt-3 text-sm">
            {t('noDocuments', {
              defaultValue:
                'No saved texts yet. Import a short Chinese paragraph to make it tappable.'
            })}
          </BodyText>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {items.map((item) => {
          const readHref = getReadHref(item.id)
          const deleteAction = (
            <Dialog>
              <DialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-muted-foreground hover:text-destructive"
                  disabled={deletingId === item.id}
                >
                  {deletingId === item.id ? (
                    <IconLoader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <IconTrash className="h-4 w-4" />
                  )}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t('deleteDocument')}</DialogTitle>
                  <DialogDescription>
                    {t('deleteDocumentConfirm', { title: item.title })}
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => {
                      const trigger = document.querySelector(
                        '[data-slot="dialog-close"]'
                      ) as HTMLElement
                      trigger?.click()
                    }}
                  >
                    {t('cancel')}
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => {
                      void handleDelete(item)
                      const trigger = document.querySelector(
                        '[data-slot="dialog-close"]'
                      ) as HTMLElement
                      trigger?.click()
                    }}
                  >
                    {t('delete')}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )

          return (
            <ContentCard
              key={`${item.kind}-${item.id}`}
              label={item.kind === 'reader' ? 'Text' : 'Document'}
              title={item.title}
              subtitle={formatDateRelative(item.createdAt)}
              tagsText={makeSubtitle(item)}
              actionHref={readHref}
              actionLabel={t('read')}
              rightAction={deleteAction}
              className="h-full"
            />
          )
        })}
      </div>
    </div>
  )
}

type DocumentListItem =
  | {
      kind: 'reader'
      id: string
      title: string
      createdAt: string
      content: StoredReaderContent['content']
      source: StoredReaderContent
    }
  | {
      kind: 'legacy'
      id: string
      title: string
      createdAt: string
      content: UserDocument['content']
      source: UserDocument
    }
